/**
 * EmailNotificationConfig Component
 *
 * Allows tenants to enable/disable email notifications for new leads.
 * Email notifications are sent to the tenant owner's email address.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Loader2, Mail, Save } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface EmailNotificationConfigProps {
  tenantId: string;
  emailNotificationsEnabled: boolean;
  ownerEmail?: string;
  onUpdate: () => void;
}

export function EmailNotificationConfig({
  tenantId,
  emailNotificationsEnabled: initialEnabled,
  ownerEmail,
  onUpdate,
}: EmailNotificationConfigProps) {
  const { getToken } = useAuth();
  const [enabled, setEnabled] = useState(initialEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    setHasChanges(enabled !== initialEnabled);
  }, [enabled, initialEnabled]);

  // Reset save success indicator after showing
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update via PostgreSQL API
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/tenants/${tenantId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          emailNotificationsEnabled: enabled,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save email notification config');
      }

      onUpdate();
      setHasChanges(false);
      setSaveSuccess(true);
    } catch (error) {
      console.error('Failed to save email notification config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="email-enabled" className="flex items-center gap-2">
            Enable Email Notifications
            {enabled && (
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-200"
              >
                Active
              </Badge>
            )}
          </Label>
          <p className="text-sm text-muted-foreground">
            Receive an email notification when a new lead is submitted
          </p>
        </div>
        <Switch
          id="email-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
        />
      </div>

      {/* Recipient Info */}
      {ownerEmail && (
        <div className="rounded-lg bg-muted/50 p-4">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Notification Recipient</p>
              <p className="text-sm text-muted-foreground">
                Emails will be sent to: <span className="font-medium text-foreground">{ownerEmail}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                This is the account owner's email address. Contact support to change the recipient.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email Content Preview */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Email Content</h4>
        <p className="text-sm text-muted-foreground">
          Each notification email includes:
        </p>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
          <li>Lead's contact information (name, phone, email)</li>
          <li>Project details (room type, timeline, address)</li>
          <li>Estimated quote range</li>
          <li>Direct link to view the lead in your dashboard</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-end gap-3">
        {saveSuccess && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            Saved
          </span>
        )}
        <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
